import i18n from 'i18next';
import Uppy, { type Meta, type UppyFile } from '@uppy/core';
import XHRUpload from '@uppy/xhr-upload';
import GoldenRetriever from '@uppy/golden-retriever';
import { getTokenFromLocalStorage } from '_utils/BaseApi';
import {
  setProgress,
  setStatus,
  setSummary,
  resetUpload,
} from '_services/Redux/slices/uploadSlice';
import { addNotification } from '@consolidocs-ui/utils';
import { track } from '@consolidocs-ui/analytics';
import { dispatch } from '_services/Redux/store';
import { FileAndFolderApi } from '@consolidocs-ui/data-access/FileAndFolderApi';

let toastCallback:
  | ((args: { message: string; type: 'success' | 'danger' }) => void)
  | null = null;

export function setToastCallback(cb: typeof toastCallback) {
  toastCallback = cb;
}

// Twelve queries carry the 'files' or 'dashboard' tags, so invalidating per
// file response refetched all of them once per uploaded file — thousands of
// GETs on a large batch, competing with the uploads for the browser's
// connection pool. Record that a refresh is owed and flush it once instead.
let hasUnrefreshedUploads = false;

/**
 * Invalidate the file/dashboard caches, but only if files have actually landed
 * since the last refresh. Called on 'complete' and on modal close, so the guard
 * keeps whichever fires second from issuing a duplicate round of GETs.
 */
export const flushFileCacheRefresh = () => {
  if (!hasUnrefreshedUploads) return;
  hasUnrefreshedUploads = false;
  dispatch(FileAndFolderApi.util.invalidateTags(['files', 'dashboard']));
};

const uppy = new Uppy({
  restrictions: { maxNumberOfFiles: 1000 },
});

uppy.use(XHRUpload, {
  endpoint: `${import.meta.env.VITE_BACKEND_URL}upload-to-s3/`,
  fieldName: 'file',
  formData: true,
  bundle: false,
  // One request per file (bundle: false) keeps per-file meta, progress and
  // error reporting intact; raising the concurrency above XHRUpload's default
  // of 5 is what speeds up large batches. Browsers cap out around 6 parallel
  // connections per origin on HTTP/1.1, so 10 only pays off over HTTP/2.
  limit: 5,
  // A header object would freeze the token read at module-evaluation time,
  // i.e. at app startup, so uploads kept sending the boot-time token and 401'd
  // for the rest of the session once it rotated. XHRUpload re-invokes this
  // function per request, matching how axiosBaseQuery reads the token.
  headers: () => ({
    authorization: `Bearer ${getTokenFromLocalStorage()}`,
  }),
  onAfterResponse() {
    hasUnrefreshedUploads = true;
  },
});
uppy.use(GoldenRetriever);

const isInFlight = (file: UppyFile<Meta, Record<string, never>>) =>
  Boolean(file.progress?.uploadStarted) && !file.progress?.uploadComplete;

/**
 * Read from Uppy, not the `upload` slice: the slice can still say 'uploading'
 * after a cancel (see the 'progress' listener below).
 */
export const isUploadInProgress = (): boolean =>
  uppy.getFiles().some(isInFlight);

export const getUploadingContractTypeId = (): string | undefined =>
  uppy.getFiles().find(isInFlight)?.meta.contract_type_id as string | undefined;

let listenersBound = false;

export const bindGlobalUppyListeners = () => {
  if (listenersBound) return;
  listenersBound = true;

  uppy.on('restored', async () => {
    // Don't auto-upload restored files - let user manually trigger upload
    if (uppy.getFiles().length > 0) {
      console.error('Files restored from previous session:', uppy.getFiles().length);
    }
  });

  uppy.on('file-added', () => {
    dispatch(setStatus('idle'));
    dispatch(setProgress(0));
  });

  uppy.on('progress', (totalProgress: number) => {
    // Core throttles this tick at 500ms, so one lands after cancelAll() has
    // emptied the queue and reset the status. Ungated it would pin the status
    // on 'uploading' for good, since nothing emits afterwards.
    if (!isUploadInProgress()) return;
    dispatch(setStatus('uploading'));
    dispatch(setProgress(totalProgress));
  });

  uppy.on('complete', (result) => {
    flushFileCacheRefresh();

    if (!result || !result.successful || !result.failed) {
      dispatch(resetUpload());
      return;
    }

    dispatch(
      setSummary({
        successful: result.successful.length,
        failed: result.failed.length,
      })
    );
    track('Files Uploaded', {
      successful: result.successful.length,
      failed: result.failed.length,
    });
    dispatch(setProgress(100));
    dispatch(setStatus('success'));
    setTimeout(() => {
      dispatch(resetUpload());
    }, 2000);
    uppy.clear();

    if (result.failed.length > 0) {
      dispatch(setStatus('error'));

      // if (toastCallback) {
      //   toastCallback({
      //     message: `${result.failed.length} file(s) failed to upload.`,
      //     type: 'danger',
      //   });
      // }
    } else {
      dispatch(setStatus('success'));

      if (toastCallback) {
        toastCallback({
          message: i18n.t('Files uploaded successfully!'),
          type: 'success',
        });
      }
    }
  });

  // Global fallback for errors not tied to a specific file. Per-file upload
  // failures are surfaced by the 'upload-error' handler below, so we only log
  // here to avoid stacking a generic toast on top of the precise per-file one.
  uppy.on('error', (error) => {
    console.error('Uppy upload error:', error);
    dispatch(setStatus('error'));
  });

  uppy.on('upload-error', (file, _error, response) => {
    dispatch(setStatus('error'));
    let apiMessage = 'Upload failed';

    // XHRUpload attaches the raw XHR response (responseText/statusText), which
    // isn't part of Uppy's core response type, so we read it through a narrow cast.
    const xhrResponse = response as
      | { responseText?: string; statusText?: string }
      | undefined;

    try {
      if (xhrResponse?.responseText) {
        const parsed = JSON.parse(xhrResponse.responseText);
        apiMessage = parsed.message || parsed.error || apiMessage;
      }
    } catch (e) {
      apiMessage = xhrResponse?.statusText || apiMessage;
    }
    const finalMessage = `${file?.name} — ${apiMessage}`;

    // Keep a persistent record in the header notification center in addition
    // to the transient toast, so users can review errors after they dismiss.
    dispatch(addNotification({ message: finalMessage, type: 'danger' }));

    if (toastCallback) {
      toastCallback({
        message: finalMessage,
        type: 'danger',
      });
    }
  });
};

export default uppy;
